// Rapportvakt — local persistence.
//
// Subscriptions live on the server; what is kept here is a cache of the last
// known answer (the email and the series slug) so the app can paint the right
// state on the first frame instead of after a round trip. The server always
// wins on reconciliation. Every access is wrapped because Safari in private
// mode throws on access, and a thrown storage error must never take the
// product surface down with it.

// v1 (rapportvakt.pick.v1) held a single { seriesId, email, savedAt } — one
// reader, one watch. Readers can now hold several, so it is replaced by an
// array under its own key rather than migrated; a stray v1 key from before
// this change is simply never read again.
const WATCHES_KEY = 'rapportvakt.watches.v1';
// The reader's identity used to just be this cached address — there was no
// real auth. Now it is a session token (below); this key survives only as a
// display cache, so a sign-in form (or the avatar, for the moment before
// /auth/me answers) can show the last address without waiting on the network.
const EMAIL_KEY = 'rapportvakt.email.v1';
const LANG_KEY = 'rapportvakt.lang.v1';
// The bearer session magic-link login hands the browser: { token, email,
// tier, limit }. Only the token is sent to the server; email/tier/limit are
// carried along so the account context can paint immediately on reload
// instead of waiting for /auth/me to answer first.
const SESSION_KEY = 'rapportvakt.session.v1';

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable. State stays in memory for this session.
  }
}

/** Array of { seriesId, email }, one per catalogue row the reader watches. */
export function loadWatches() {
  const value = readJson(WATCHES_KEY, []);
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item) => item && typeof item === 'object' && typeof item.seriesId === 'string'
  );
}

/** Pass [] to clear every watch (e.g. on logout). */
export function saveWatches(watches) {
  writeJson(WATCHES_KEY, Array.isArray(watches) ? watches : []);
}

/**
 * The address the visitor last used. Outlives the pick itself: after removing a
 * watch we still know who they are, so the next pick form comes prefilled and
 * the next reload can still ask the server what they are subscribed to.
 */
export function loadEmail() {
  const value = readJson(EMAIL_KEY, null);
  return typeof value === 'string' && value ? value : '';
}

export function saveEmail(email) {
  writeJson(EMAIL_KEY, email || null);
}

export function loadLang() {
  const value = readJson(LANG_KEY, null);
  return value === 'sv' || value === 'en' ? value : 'sv';
}

export function saveLang(lang) {
  writeJson(LANG_KEY, lang);
}

/** { token, email, tier, limit }, or null when signed out. */
export function loadSession() {
  const value = readJson(SESSION_KEY, null);
  if (!value || typeof value !== 'object' || typeof value.token !== 'string' || !value.token) {
    return null;
  }
  return value;
}

/** Pass null (or nothing) to sign out locally. */
export function saveSession(session) {
  writeJson(SESSION_KEY, session || null);
}
